<template>
  <Modal
      :visible="visible"
      :style="computedWindowSize"
      class="p-fluid plan-change-modal"
      @close="close">

    <template #header>
      <div class="p-dialog-title">
        {{ $t(isTrial ? 'billing.planChangeModal.trialTitle' : 'billing.planChangeModal.title') }}
      </div>
    </template>

    <div v-if="subscription" class="plan-change-modal__content">
      <div class="plan-change-modal__context p-mb-3">
        <div class="plan-change-modal__location">
          {{ subscription.location_name || '—' }}
        </div>

        <div class="plan-change-modal__meta p-text-secondary">
          <span v-if="subscription.company_name">
            {{ subscription.company_name }}
          </span>

          <span
              v-if="subscription.company_name"
              class="plan-change-modal__separator">
            ·
          </span>

          <span v-if="isTrial">{{ $t('billing.subscriptions.trialAccessLabel') }}</span>
          <span v-else>
            {{ $t('billing.planChangeModal.currentPlanLabel') }}
            <strong>{{ currentPlanKey }}</strong>
          </span>

          <template v-if="subscription.current_period_end">
            <span class="plan-change-modal__separator"> · </span>
            <span>
              {{ $t('billing.planChangeModal.validThroughLabel') }}
              {{ formatUtcDay(subscription.current_period_end - 1) }}
            </span>
          </template>
        </div>

        <div
            v-if="subscription.has_pending_plan"
            class="plan-change-modal__pending p-mt-1">
          {{ $t('billing.planChangeModal.scheduledPlanLabel') }}
          <strong>{{ subscription.pending_plan_key }}</strong>

          <span v-if="subscription.pending_plan_starts_at">
             · {{ $t('billing.planChangeModal.fromDateLabel') }}
            {{ formatUtcDay(subscription.pending_plan_starts_at) }}
          </span>
        </div>
      </div>

      <div v-if="step === 1 && requiredFeatureLabel && minimumPlanKey"
           class="plan-change-modal__feature-notice p-mb-3">
        <i class="pi pi-info-circle p-mr-2"/>
        <span>
          {{ $t('billing.planChangeModal.requiredFeaturePrefix', { feature: requiredFeatureLabel }) }}
          <strong>{{ minimumPlanKey }}</strong>.
        </span>
      </div>

      <template v-if="step === 1">
        <div
            ref="tableScroll"
            class="plan-change-modal__table-scroll">
          <table class="plan-comparison-table">
            <thead>
            <tr>
              <th class="plan-comparison-table__feature-column"/>
              <th v-for="plan in plans"
                  :key="plan.key"
                  :class="planColumnClass(plan.key)"
                  @click="selectPlan(plan.key)">
                <div class="plan-comparison-table__plan-header">
                  <span class="plan-comparison-table__plan-name">
                    {{ plan.key }}<span v-if="plan.recommended"> ★</span>
                  </span>

                  <span class="plan-comparison-table__price-line">
                    <span class="plan-comparison-table__plan-price">
                      {{ formatBillingCurrency(plan.price, 'EUR', 0, 2) }}
                    </span>
                    <span class="plan-comparison-table__plan-period">
                      {{ $t('billing.planChangeModal.pricePeriodShort') }}
                    </span>
                  </span>

                  <div class="plan-comparison-table__plan-action">
                    <span v-if="activationMode && plan.key === selectedPlanKey"
                          class="plan-comparison-table__plan-badge">
                      {{ $t('billing.planChangeModal.selectedBadge') }}
                    </span>

                    <span v-else-if="cancelPendingMode && plan.key === currentPlanKey"
                          class="plan-comparison-table__plan-badge">
                      {{ $t('billing.planChangeModal.keepCurrentPlanButton', { plan: currentPlanKey }) }}
                    </span>
                    <Button v-else-if="!activationMode && subscription.has_pending_plan && plan.key === currentPlanKey"
                            :label="$t('billing.planChangeModal.keepCurrentPlanButton', { plan: currentPlanKey })"
                            style="width: auto"
                            class="p-button-outlined p-button-sm"
                            @click.stop="selectPlan(plan.key)"/>

                    <span v-else-if="plan.key === currentPlanKey"
                          class="plan-comparison-table__plan-badge">
                      {{ $t('billing.planChangeModal.currentBadge') }}
                    </span>

                    <span v-else-if="plan.key === selectedPlanKey"
                          class="plan-comparison-table__plan-badge">
                      {{ $t('billing.planChangeModal.selectedBadge') }}
                    </span>

                    <Button v-else
                            :label="$t('billing.planChangeModal.selectButton')"
                            style="width: auto"
                            class="p-button-outlined p-button-sm"
                            @click.stop="selectPlan(plan.key)"/>
                  </div>
                </div>
              </th>
            </tr>
            </thead>

            <tbody>
            <template v-for="section in sections" :key="section.key">
              <tr v-if="section.key === 'statistics'"
                  class="plan-comparison-table__section-row">
                <td class="plan-comparison-table__feature-column plan-comparison-table__section-toggle"
                    @click="toggleSection(section.key)">
                  <div class="p-d-flex p-ai-center">
                    <span>{{ section.label }}</span>
                    <i class="pi p-ml-2"
                       :class="expandedSections.statistics
                        ? 'pi-chevron-up'
                        : 'pi-chevron-down'"/>
                  </div>
                </td>

                <td v-for="plan in plans"
                    :key="`statistics-level-${plan.key}`"
                    class="plan-comparison-table__plan-cell"
                    :class="planColumnClass(plan.key)"
                    @click="selectPlan(plan.key)">
                  {{ section.values[plan.key] }}
                </td>
              </tr>

              <template v-if="section.key !== 'statistics' || expandedSections.statistics">
                <tr v-for="row in section.rows"
                    :key="row.key"
                    :data-feature-key="row.key"
                    class="plan-comparison-table__feature-row"
                    :class="{
                     'plan-comparison-table__feature-row--divider': [
                        'outgoing_email',
                        'accounting_export',
                        'supplier_access',
                      ].includes(row.key),
                      'plan-comparison-table__feature-row--required': isRequiredFeature(row.key),
                    }">

                  <td class="plan-comparison-table__feature-column">
                    <div class="plan-comparison-table__feature-label">
                      <span>{{ row.label }}</span>

                      <button v-if="row.tooltip"
                              type="button"
                              class="plan-comparison-table__info"
                              :aria-label="$t('billing.planChangeModal.featureInfoAriaLabel')"
                              @click.stop="showFeatureTooltip($event, row.tooltip)">
                        <i class="pi pi-info-circle"/>
                      </button>
                    </div>
                  </td>

                  <td
                      v-if="row.sharedValue"
                      colspan="4"
                      class="plan-comparison-table__shared-value">
                    {{ row.sharedValue }}
                  </td>

                  <template v-else>
                    <td
                        v-for="plan in plans"
                        :key="`${row.key}-${plan.key}`"
                        class="plan-comparison-table__plan-cell"
                        :class="planColumnClass(plan.key)"
                        @click="selectPlan(plan.key)">
                      <span class="plan-comparison-table__value">
                        <span
                            v-if="row.values[plan.key] === true"
                            class="plan-comparison-table__value--yes">
                          ✓
                        </span>

                        <i v-else-if="row.values[plan.key] === false || row.values[plan.key] == null"
                           class="ti-minus plan-comparison-table__value--no"/>

                        <span v-else>
                          {{ row.values[plan.key] }}
                        </span>
                      </span>
                    </td>
                  </template>
                </tr>
              </template>
            </template>
            </tbody>

            <tfoot>
            <tr class="plan-comparison-table__bottom-actions">
              <td class="plan-comparison-table__feature-column"/>

              <td v-for="plan in plans"
                  :key="`bottom-action-${plan.key}`"
                  class="plan-comparison-table__plan-cell"
                  :class="planColumnClass(plan.key)"
                  @click="selectPlan(plan.key)">
                <span v-if="cancelPendingMode && plan.key === currentPlanKey"
                      class="plan-comparison-table__plan-badge">
                  {{
                    $t('billing.planChangeModal.keepCurrentPlanButton', {
                      plan: currentPlanKey,
                    })
                  }}
                </span>

                <span v-else-if="plan.key === selectedPlanKey"
                      class="plan-comparison-table__plan-badge">
                  {{
                    activationMode
                        ? $t('billing.planChangeModal.selectedBadge')
                        : (
                            plan.key === currentPlanKey
                                ? $t('billing.planChangeModal.currentBadge')
                                : $t('billing.planChangeModal.selectedBadge')
                        )
                  }}
                </span>

                <Button v-else
                        :label="subscription.has_pending_plan && plan.key === currentPlanKey
                          ? $t('billing.planChangeModal.keepCurrentPlanButton', {
                              plan: currentPlanKey,
                            })
                          : $t('billing.planChangeModal.selectButton')"
                        style="width: auto"
                        class="p-button-outlined p-button-sm"
                        @click.stop="selectPlan(plan.key)"/>
              </td>
            </tr>
            </tfoot>
          </table>
        </div>

        <div class="plan-change-modal__notes p-mt-3">
          {{ $t('billing.planChangeModal.pricesNote') }}
        </div>

        <OverlayPanel ref="featureTooltipOverlay">
          <div class="plan-change-modal__tooltip-content">
            {{ activeFeatureTooltip }}
          </div>
        </OverlayPanel>
      </template>

      <template v-else>
        <div class="plan-change-confirmation">
          <div class="plan-change-confirmation__title p-mb-4">
            {{$t(cancelPendingMode
              ? 'billing.planChangeModal.cancelPendingConfirmationTitle'
              : (isTrial
                  ? 'billing.planChangeModal.trialConfirmationTitle'
                  : 'billing.planChangeModal.confirmationTitle')
          ) }}
          </div>

          <div class="plan-change-confirmation__row">
            <span class="p-text-secondary">
              {{ $t('billing.planChangeModal.locationLabel') }}
            </span>
            <strong>{{ subscription.location_name || '—' }}</strong>
          </div>

          <div class="plan-change-confirmation__row">
            <span class="p-text-secondary">
              {{ $t(isTrial ? 'billing.planChangeModal.selectedPlanLabel' : 'billing.planChangeModal.changeLabel') }}
            </span>
            <!--            <strong>{{ currentPlanKey }} → {{ selectedPlanKey }}</strong>-->
            <!--            <strong v-if="reactivationMode">{{ selectedPlanKey }}</strong>-->
            <!--            <strong v-else>{{ currentPlanKey }} → {{ selectedPlanKey }}</strong>-->
            <strong v-if="cancelPendingMode">
              {{ pendingPlanKey }} → {{ currentPlanKey }}
            </strong>

            <strong v-else-if="activationMode">
              {{ selectedPlanKey }}
            </strong>

            <strong v-else>
              {{ currentPlanKey }} → {{ selectedPlanKey }}
            </strong>
          </div>

          <div v-if="!hasMonthlyBreakdown" class="plan-change-confirmation__row">
            <span class="p-text-secondary">{{ $t('billing.planChangeModal.locationPlanPriceLabel') }}</span>
            <strong>{{ formatBillingCurrency(selectedPlan.price, 'EUR', 0, 2) }}{{ $t('billing.planChangeModal.pricePeriodShort') }}</strong>
          </div>

          <div v-if="hasMonthlyBreakdown" class="plan-change-costs p-mt-3">
            <table class="plan-change-costs__table"
                   :class="{'plan-change-costs__table--comparison': invoicePreview.monthly_breakdown_before}">
              <caption>{{ monthlyCostCaption }}</caption>
              <colgroup>
                <col class="plan-change-costs__label-column"/>
                <col v-for="column in monthlyCostColumns" :key="column.key" class="plan-change-costs__value-column"/>
              </colgroup>
              <thead v-if="invoicePreview.monthly_breakdown_before">
                <tr>
                  <th scope="col" :aria-label="$t('billing.planChangeModal.costItem')"></th>
                  <th v-if="invoicePreview.monthly_breakdown_before" scope="col">{{ monthlyCostBeforeLabel }}</th>
                  <th scope="col">{{ monthlyCostAfterLabel }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in monthlyCostRows" :key="row.key" :class="{'plan-change-costs__total': row.key === 'wholeAccountMonthlyTotal'}">
                  <th scope="row">{{ $t('billing.planChangeModal.' + row.key) }}</th>
                  <td v-for="column in monthlyCostColumns" :key="column.key">
                    <div class="plan-change-costs__amount">
                      {{ formatBillingCurrency(column.value[row.amount], invoicePreview.currency || 'EUR', 0, 2) }}
                    </div>
                    <div v-if="row.amount === 'location_base_total' && column.discount?.active"
                         class="plan-change-costs__discount p-text-secondary">
                      <div class="plan-change-costs__discount-price">
                        <del>{{ formatBillingCurrency(column.discount.base_price, invoicePreview.currency || 'EUR', 0, 2) }}</del>
                        <span>−{{ formatPercent(column.discount.percent) }}%</span>
                      </div>
                      <div v-if="column.discount.ends_at">
                        {{ $t('billing.subscriptions.untilDateLabel') }} {{ formatUtcDay(column.discount.ends_at - 1) }}
                      </div>
                    </div>
                    <div v-if="row.extra && column.value[row.extra] > 0"
                         class="plan-change-costs__calculation p-text-secondary">
                      {{ column.value[row.extra] }} × {{ formatBillingCurrency(column.value[row.amount] / column.value[row.extra], invoicePreview.currency || 'EUR', 0, 2) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="hasMonthlyComparison" class="plan-change-costs plan-change-cost-changes p-mt-3">
            <table class="plan-change-costs__table plan-change-costs__table--changes">
              <caption>{{ $t('billing.planChangeModal.monthlyChanges') }}</caption>
              <colgroup>
                <col class="plan-change-costs__label-column"/>
                <col class="plan-change-costs__value-column"/>
              </colgroup>
              <tbody>
                <tr v-for="change in monthlyCostChanges" :key="change.key">
                  <th scope="row">
                    {{ $t('billing.planChangeModal.' + change.key) }}
                    <div v-if="change.extra" class="plan-change-costs__calculation">
                      {{ $t('billing.planChangeModal.resourceAllowanceChange', {
                        beforeIncluded: change.beforeIncluded,
                        afterIncluded: change.afterIncluded,
                        beforeExtra: change.beforeExtra,
                        afterExtra: change.afterExtra,
                      }) }}
                    </div>
                  </th>
                  <td :class="{'plan-change-costs__saving': change.amount < 0}">
                    {{ formatSignedMonthlyAmount(change.amount) }}
                  </td>
                </tr>
                <tr class="plan-change-costs__total">
                  <th scope="row">{{ $t('billing.planChangeModal.accountMonthlyChange') }}</th>
                  <td :class="{'plan-change-costs__saving': monthlyAccountChange < 0}">
                    {{ formatSignedMonthlyAmount(monthlyAccountChange) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="showsInvoicePreview && invoicePreview" class="plan-change-confirmation__row plan-change-confirmation__invoice">
            <span class="p-text-secondary">{{ invoicePreviewAmountLabel }}</span>
            <strong>{{ formatBillingCurrency(invoicePreview.invoice_total, invoicePreview.currency || 'EUR') }}</strong>
          </div>

          <div v-if="showsInvoicePreview && invoicePreview" class="plan-change-confirmation__row plan-change-confirmation__period">
            <span class="p-text-secondary">{{ invoicePreviewPeriodLabel }}</span>
            <strong>{{ formatUtcDay(invoicePreview.period_start) }} – {{ formatUtcDay(invoicePreview.period_end - 1) }}</strong>
          </div>

          <div v-if="!cancelPendingMode" class="plan-change-confirmation__row plan-change-confirmation__effective">
            <span class="p-text-secondary">{{ $t('billing.planChangeModal.effectiveDateLabel') }}</span>
            <strong>{{ effectiveDateLabel }}</strong>
          </div>

          <div class="plan-change-confirmation__notice p-mt-4">
            <template v-if="isTrial">
              {{
                $t(
                    trialPlanSelectionOnly
                        ? 'billing.planChangeModal.trialSelectionNotice'
                        : 'billing.planChangeModal.trialActivationNotice'
                )
              }}
            </template>

            <template v-else-if="changeDirection === 'activation'">
              {{ $t('billing.planChangeModal.activationNotice') }}
            </template>

            <template v-else-if="changeDirection === 'upgrade'">
              {{ $t('billing.planChangeModal.upgradeNotice') }}
            </template>

            <template v-else-if="cancelPendingMode">
              {{ $t('billing.planChangeModal.cancelPendingNotice', { scheduledPlan: pendingPlanKey, currentPlan: currentPlanKey }) }}
            </template>

            <template v-else>
              {{
                $t('billing.planChangeModal.downgradeNotice', {
                  currentPlan: currentPlanKey,
                  currentPlanValidThrough: currentPlanValidThroughLabel,
                  effectiveDate: effectiveDateLabel,
                  selectedPlan: selectedPlanKey,
                })
              }}
            </template>
          </div>

          <p v-if="hasMonthlyBreakdown" class="plan-change-confirmation__cost-note p-text-secondary">
            {{ monthlyCostNote }}
          </p>

          <details v-if="hasMonthlyBreakdown" class="plan-change-cost-details p-mt-3">
            <summary>{{ $t('billing.planChangeModal.calculationDetails') }}</summary>
            <div class="plan-change-cost-details__content">
              <table class="plan-change-costs__table plan-change-costs__table--details"
                     :class="{'plan-change-costs__table--comparison': invoicePreview.monthly_breakdown_before}">
                <colgroup>
                  <col class="plan-change-costs__label-column"/>
                  <col v-for="column in monthlyCostColumns" :key="column.key" class="plan-change-costs__value-column"/>
                </colgroup>
                <thead v-if="invoicePreview.monthly_breakdown_before">
                  <tr>
                    <th scope="col">{{ $t('billing.planChangeModal.costItem') }}</th>
                    <th v-if="invoicePreview.monthly_breakdown_before" scope="col">{{ monthlyCostBeforeLabel }}</th>
                    <th scope="col">{{ monthlyCostAfterLabel }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="hasOtherBillableLocations">
                    <th scope="row">{{ $t('billing.planChangeModal.allLocationPlans') }}</th>
                    <td v-for="column in monthlyCostColumns" :key="column.key">
                      {{ formatBillingCurrency(column.value.base_total, invoicePreview.currency || 'EUR', 0, 2) }}
                    </td>
                  </tr>
                  <tr v-for="row in monthlyResourceRows" :key="row.key">
                    <th scope="row">{{ $t('billing.planChangeModal.' + row.detailKey) }}</th>
                    <td v-for="column in monthlyCostColumns" :key="column.key">
                      {{ $t('billing.planChangeModal.resourceUsage', {
                        used: column.value[row.used], included: column.value[row.included]
                      }) }}
                      <div v-if="column.value[row.extra] > 0" class="plan-change-costs__calculation">
                        {{ column.value[row.extra] }} × {{ formatBillingCurrency(column.value[row.amount] / column.value[row.extra], invoicePreview.currency || 'EUR', 0, 2) }} = {{ formatBillingCurrency(column.value[row.amount], invoicePreview.currency || 'EUR', 0, 2) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>

          <div v-if="requiresPlanChangeConfirmation" class="p-d-flex p-ai-center p-mt-4">
            <Checkbox v-model="planChangeConfirmed"
                      id="plan-change-confirmation"
                      :binary="true"
                      :disabled="isSubmitting"/>
            <div>
              <label for="plan-change-confirmation">
                <div class="pointer p-p-1 p-ml-1">
                  {{ planChangeConfirmationLabel }}
                </div>
              </label>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div v-if="step === 1"
         class="plan-change-modal__footer-summary p-mt-2">
      <div>
        <span class="p-text-secondary">
          {{ $t('billing.planChangeModal.selectedPlanLabel') }}:
        </span>

        <strong class="p-ml-1">
          {{ selectedPlan.key }}
        </strong>

        <span class="p-ml-2">
          {{ formatBillingCurrency(selectedPlan.price, 'EUR', 0, 2) }}{{ $t('billing.planChangeModal.pricePeriodShort') }}
        </span>
      </div>

      <div class="plan-change-modal__footer-hint">
        {{ selectionHint }}
      </div>
    </div>

    <template #footer>
      <Button v-if="step === 2"
              :label="$t('billing.planChangeModal.backButton')"
              icon="pi pi-arrow-left"
              class="p-button-text"
              :disabled="isSubmitting"
              @click="step = 1"/>

      <Button :label="$t('billing.planChangeModal.cancelButton')"
              icon="pi pi-times"
              class="p-button-text"
              :disabled="isSubmitting"
              @click="close"/>

      <Button v-if="step === 1"
              :label="$t('billing.planChangeModal.continueButton')"
              class="p-button-raised"
              icon="pi pi-arrow-right"
              iconPos="right"
              :loading="invoicePreviewIsLoading"
              :disabled="!canContinue || invoicePreviewIsLoading"
              @click="continueToConfirmation"/>

      <Button v-else
              class="p-button-raised"
              :label="confirmButtonLabel"
              icon="pi pi-check"
              :loading="isSubmitting"
              :disabled="isSubmitting || (requiresPlanChangeConfirmation && !planChangeConfirmed)"
              @click="confirm"/>
    </template>
  </Modal>
</template>

<script>
import formatMixins from '@/mixins/formatMixins'
import httpClient from '@/services/http.services'
import {planChangeErrorMessage} from '@/services/billingPlanErrors'
import settings from '@/settings'

export default {
  name: 'PlanChangeModal',
  emits: ['close', 'confirm'],
  mixins: [formatMixins],
  props: {
    visible: Boolean,
    subscription: {
      type: Object,
      default: null,
    },
    requiredFeatureKey: {
      type: String,
      default: '',
    },
    requiredFeatureLabel: {
      type: String,
      default: '',
    },
    minimumPlanKey: {
      type: String,
      default: '',
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    isTrial: {
      type: Boolean,
      default: false,
    },
    trialSelectionCompletesActivation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      planChangeConfirmed: false,
      activeFeatureTooltip: '',
      activeFeatureTooltipTarget: null,
      featureTooltipTimer: null,
      step: 1,
      selectedPlanKey: null,
      invoicePreview: null,
      invoicePreviewIsLoading: false,
      expandedSections: {
        statistics: false,
      },
      plans: [
        {key: 'LITE', price: 40, order: 1},
        {key: 'BASIC', price: 70, order: 2},
        {key: 'STANDARD', price: 100, order: 3, recommended: true},
        {key: 'PRO', price: 140, order: 4},
      ],
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.reset()
      }
    },
    subscription: {
      immediate: true,
      handler() {
        if (this.visible) {
          this.reset()
        }
      },
    },
  },
  methods: {
    monthlyAmountDifference(before, after) {
      return (Math.round(Number(after || 0) * 100) - Math.round(Number(before || 0) * 100)) / 100
    },
    formatSignedMonthlyAmount(value) {
      const amount = Number(value || 0)
      const sign = amount > 0 ? '+' : amount < 0 ? '−' : ''
      return sign + this.formatBillingCurrency(Math.abs(amount), this.invoicePreview?.currency || 'EUR', 0, 2)
    },
    formatPercent(value) {
      const number = Number(value || 0)
      return Number.isInteger(number)
          ? number
          : number.toFixed(2).replace(/\.00$/, '')
    },
    reset() {
      this.step = 1
      this.selectedPlanKey = this.pendingPlanKey || this.currentPlanKey || 'LITE'
      this.planChangeConfirmed = false
      this.invoicePreview = null
      this.invoicePreviewIsLoading = false
      this.scrollToRequiredFeature()
    },
    close() {
      if (this.isSubmitting || this.invoicePreviewIsLoading) return
      this.$emit('close')
    },
    selectPlan(planKey) {
      if (this.selectedPlanKey !== planKey) {
        this.planChangeConfirmed = false
        this.invoicePreview = null
      }

      this.selectedPlanKey = planKey
    },
    async continueToConfirmation() {
      if (!this.canContinue || this.invoicePreviewIsLoading) return

      this.planChangeConfirmed = false

      if (!this.shouldLoadInvoicePreview) {
        this.step = 2
        return
      }

      this.invoicePreviewIsLoading = true

      try {
        const {status, data} = await httpClient.post(
            'billing/plan-change-preview',
            {
              subscription_id: Number(
                  this.subscription?.subscription_id || 0
              ),
              location_id: Number(this.subscription?.location_id || 0),
              to_plan_key: this.selectedPlanKey,
            }
        )

        if (status !== 200 || !data?.success) {
          this.showUpgradePreviewError(data?.message, data?.code)
          return
        }

        this.invoicePreview = data
        this.step = 2
      } catch (err) {
        this.showUpgradePreviewError(
            err?.response?.data?.message || err?.message,
            err?.response?.data?.code
        )
      } finally {
        this.invoicePreviewIsLoading = false
      }
    },
    showUpgradePreviewError(message = '', code = null) {
      this.$toast.add({
        severity: 'error',
        detail: planChangeErrorMessage({message, code}, (key) => this.$t(key)),
        life: settings.toastLife,
      })
    },
    showFeatureTooltip(event, tooltip) {
      if (!event || !tooltip) return

      const overlay = this.$refs.featureTooltipOverlay
      const target = event.currentTarget

      if (!overlay || !target) return

      clearTimeout(this.featureTooltipTimer)

      if (
          overlay.visible
          && this.activeFeatureTooltipTarget === target
      ) {
        overlay.hide()
        this.activeFeatureTooltipTarget = null
        return
      }

      this.activeFeatureTooltip = tooltip
      this.activeFeatureTooltipTarget = target

      const showOverlay = () => {
        overlay.show(event, target)
        this.featureTooltipTimer = null
      }

      if (overlay.visible) {
        overlay.hide()
        this.featureTooltipTimer = setTimeout(showOverlay, 150)
      } else {
        this.$nextTick(showOverlay)
      }
    },
    toggleSection(sectionKey) {
      this.expandedSections[sectionKey] = !this.expandedSections[sectionKey]
    },
    isRequiredFeature(featureKey) {
      return featureKey === this.normalizedRequiredFeatureKey
    },
    scrollToRequiredFeature() {
      if (!this.normalizedRequiredFeatureKey) return

      this.$nextTick(() => {
        const tableScroll = this.$refs.tableScroll
        const rows = tableScroll?.querySelectorAll('[data-feature-key]') || []
        const row = Array.from(rows).find(item => {
          return item.dataset.featureKey === this.normalizedRequiredFeatureKey
        })

        if (!row) return

        row.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        })
      })
    },
    confirm() {
      if (this.isSubmitting) return

      if (this.requiresPlanChangeConfirmation && !this.planChangeConfirmed) {
        return
      }

      this.$emit('confirm', {
        subscription_id: Number(this.subscription?.subscription_id || 0),
        location_id: Number(this.subscription?.location_id || 0),
        from_plan_key: this.currentPlanKey,
        to_plan_key: this.selectedPlanKey,
        direction: this.changeDirection,
        effective_at: this.changeDirection === 'downgrade'
            ? Number(this.subscription?.next_billing_period_start || 0)
            : null,
      })
    },
    planColumnClass(planKey) {
      return {
        'plan-column--current': planKey === this.currentPlanKey,
        'plan-column--selected': planKey === this.selectedPlanKey,
      }
    },
  },
  computed: {
    normalizedRequiredFeatureKey() {
      return String(this.requiredFeatureKey || '').trim().toLowerCase()
    },
    sections() {
      const modalT = (key, params = undefined) => {
        return this.$t(`billing.planChangeModal.${key}`, params)
      }

      const featureT = (key, params = undefined) => {
        return this.$t(`billing.features.${key}`, params)
      }

      const row = (
          key,
          values = null,
          {
            tooltip = false,
            sharedValueKey = null,
          } = {}
      ) => {
        const item = {
          key,
          label: featureT(`${key}.label`),
        }

        if (values !== null) {
          item.values = values
        }

        if (tooltip) {
          item.tooltip = featureT(`${key}.tooltip`)
        }

        if (sharedValueKey) {
          item.sharedValue = featureT(`${sharedValueKey}.value`)
        }

        return item
      }

      return [
        {
          key: 'main',
          label: modalT('sections.main'),
          rows: [
            row(
                'included_employees',
                {LITE: '1', BASIC: '2', STANDARD: '3', PRO: '4'}
            ),
            row(
                'extra_employee',
                null,
                {sharedValueKey: 'extra_employee'}
            ),
            row(
                'orders_per_month',
                {
                  LITE: modalT('values.upTo60'),
                  BASIC: modalT('values.upTo120'),
                  STANDARD: modalT('values.unlimited'),
                  PRO: modalT('values.unlimited'),
                }
            ),
            row(
                'client_history',
                {LITE: true, BASIC: true, STANDARD: true, PRO: true}
            ),
            row(
                'orders',
                {LITE: true, BASIC: true, STANDARD: true, PRO: true}
            ),
            row(
                'sales_invoices',
                {LITE: true, BASIC: true, STANDARD: true, PRO: true}
            ),
            row(
                'special_order_items',
                {LITE: true, BASIC: true, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'outgoing_email',
                {LITE: true, BASIC: true, STANDARD: true, PRO: true}
            ),
          ],
        },
        {
          key: 'workshop',
          label: modalT('sections.workshop'),
          rows: [
            row(
                'email_attachments',
                {LITE: false, BASIC: true, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'scheduler',
                {LITE: false, BASIC: true, STANDARD: true, PRO: true}
            ),
            row(
                'warehouse',
                {LITE: false, BASIC: true, STANDARD: true, PRO: true}
            ),
            row(
                'work_templates',
                {LITE: false, BASIC: true, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'consumables',
                {LITE: false, BASIC: true, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'price_request_form',
                {LITE: false, BASIC: true, STANDARD: true, PRO: true}
            ),
            row(
                'visit_sms_reminder',
                {LITE: false, BASIC: true, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'accounting_export',
                {LITE: false, BASIC: true, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
          ],
        },
        {
          key: 'advanced',
          label: modalT('sections.advanced'),
          rows: [
            row(
                'incoming_mail',
                {
                  LITE: false,
                  BASIC: false,
                  STANDARD: modalT('values.oneAccount'),
                  PRO: modalT('values.twoAccounts'),
                },
                {tooltip: true}
            ),
            row(
                'extra_incoming_email_account',
                {
                  LITE: false,
                  BASIC: false,
                  STANDARD: featureT('extra_incoming_email_account.value'),
                  PRO: featureT('extra_incoming_email_account.value'),
                }
            ),
            row(
                'purchase_invoices',
                {LITE: false, BASIC: false, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'returns',
                {LITE: false, BASIC: false, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            // row(
            //     'cashbox',
            //     {LITE: false, BASIC: false, STANDARD: true, PRO: true}
            // ),
            row(
                'next_service_sms_reminder',
                {LITE: false, BASIC: false, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'detailed_change_history',
                {LITE: false, BASIC: false, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            // row(
            //     'restore_deleted_items',
            //     {LITE: false, BASIC: false, STANDARD: true, PRO: true},
            //     {tooltip: true}
            // ),
            row(
                'supplier_access',
                {LITE: false, BASIC: false, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
          ],
        },
        {
          key: 'pro',
          label: modalT('sections.pro'),
          rows: [
            // row(
            //     'service_split',
            //     {LITE: false, BASIC: false, STANDARD: false, PRO: true},
            //     {tooltip: true}
            // ),
            row(
                'booking_from_offer',
                {LITE: false, BASIC: false, STANDARD: false, PRO: true},
                {tooltip: true}
            ),
            row(
                'technician_earnings',
                {LITE: false, BASIC: false, STANDARD: false, PRO: true},
                {tooltip: true}
            ),
            row(
                'advanced_markup',
                {LITE: false, BASIC: false, STANDARD: false, PRO: true},
                {tooltip: true}
            ),
            row(
                'period_lock',
                {LITE: false, BASIC: false, STANDARD: false, PRO: true},
                {tooltip: true}
            ),
          ],
        },
        {
          key: 'statistics',
          label: modalT('sections.statistics'),
          values: {
            LITE: modalT('values.statisticsBasic'),
            BASIC: modalT('values.statisticsBasic'),
            STANDARD: modalT('values.statisticsAdvanced'),
            PRO: modalT('values.statisticsFull'),
          },
          rows: [
            row(
                'stats_invoices',
                {LITE: true, BASIC: true, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'stats_payments',
                {LITE: true, BASIC: true, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'stats_created',
                {LITE: true, BASIC: true, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'stats_technicians',
                {LITE: true, BASIC: true, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'stats_employee_sales',
                {LITE: false, BASIC: false, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'stats_consumables',
                {LITE: false, BASIC: false, STANDARD: true, PRO: true},
                {tooltip: true}
            ),
            row(
                'stats_technician_earnings',
                {LITE: false, BASIC: false, STANDARD: false, PRO: true},
                {tooltip: true}
            ),
            row(
                'stats_warranty',
                {LITE: false, BASIC: false, STANDARD: false, PRO: true},
                {tooltip: true}
            ),
            row(
                'stats_returns',
                {LITE: false, BASIC: false, STANDARD: false, PRO: true},
                {tooltip: true}
            ),
          ],
        },
      ]
    },
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: 0, width: '98%', marginTop: '10px'}
      }

      if (this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '94%', marginTop: '15px'}
      }

      return {
        position: 'absolute',
        top: 0,
        width: '94%',
        maxWidth: '1400px',
      }
    },
    currentPlanKey() {
      return String(this.subscription?.current_plan_key || '').toUpperCase()
    },
    pendingPlanKey() {
      return String(this.subscription?.pending_plan_key || '').toUpperCase()
    },
    monthlyCostColumns() {
      const columns = []
      const subscriptionId = Number(this.subscription?.subscription_id || 0)
      const addColumn = (key, breakdown, plan) => {
        if (!breakdown) return
        columns.push({
          key,
          discount: breakdown.base_discount_by_subscription?.[subscriptionId] || null,
          value: {
            ...breakdown,
            location_base_total: Number(
                breakdown.base_total_by_subscription?.[subscriptionId]
                ?? plan?.price
                ?? 0
            ),
          },
        })
      }
      addColumn('before', this.invoicePreview?.monthly_breakdown_before, this.currentPlan)
      addColumn('after', this.invoicePreview?.monthly_breakdown_after, this.selectedPlan)
      return columns
    },
    hasOtherBillableLocations() {
      return this.monthlyCostColumns.some(column =>
          Object.keys(column.value.base_total_by_subscription || {}).length > 1)
    },
    hasMonthlyBreakdown() {
      return !!this.invoicePreview?.monthly_breakdown_after
    },
    isDowngradePreview() {
      return this.invoicePreview?.preview_type === 'plan_downgrade'
    },
    downgradeEffectiveAt() {
      return this.isDowngradePreview
          ? this.invoicePreview.effective_at
          : this.subscription?.next_billing_period_start
    },
    monthlyCostCaption() {
      return this.isDowngradePreview
          ? this.$t('billing.planChangeModal.nextPeriodMonthlyCosts', {
            date: this.formatUtcDay(this.downgradeEffectiveAt),
          })
          : this.$t('billing.planChangeModal.accountMonthlyCosts')
    },
    monthlyCostBeforeLabel() {
      return this.$t('billing.planChangeModal.costBefore')
    },
    monthlyCostAfterLabel() {
      return this.$t('billing.planChangeModal.costAfter')
    },
    monthlyCostNote() {
      if (this.isDowngradePreview) {
        return this.$t('billing.planChangeModal.downgradeComparisonNote', {
          date: this.formatUtcDay(this.downgradeEffectiveAt),
        })
      }
      return this.$t(this.hasMonthlyComparison
          ? 'billing.planChangeModal.accountComparisonNote'
          : 'billing.planChangeModal.accountCostsNote')
    },
    hasMonthlyComparison() {
      return !!(this.invoicePreview?.monthly_breakdown_before && this.invoicePreview?.monthly_breakdown_after)
    },
    monthlyResourceRows() {
      return [
        { key: 'accountEmployees', changeKey: 'employeeSurchargeChange', detailKey: 'employeeUsageLabel', amount: 'extra_employees_total', extra: 'extra_employees', used: 'active_employees', included: 'included_employees' },
        { key: 'accountMail', changeKey: 'mailSurchargeChange', detailKey: 'mailUsageLabel', amount: 'extra_incoming_email_accounts_total', extra: 'extra_incoming_email_accounts', used: 'incoming_email_accounts', included: 'included_incoming_email_accounts' },
      ]
    },
    monthlyCostRows() {
      const resourceRows = !this.hasMonthlyComparison && !this.hasOtherBillableLocations
          ? this.monthlyResourceRows.filter(row =>
              this.monthlyCostColumns.some(column => Number(column.value[row.amount]) > 0))
          : []
      return [
        { key: 'locationPlanPriceLabel', amount: 'location_base_total' },
        ...resourceRows,
        { key: 'wholeAccountMonthlyTotal', amount: 'total' },
      ]
    },
    monthlyCostChanges() {
      if (!this.hasMonthlyComparison) return []
      const before = this.monthlyCostColumns.find(column => column.key === 'before').value
      const after = this.monthlyCostColumns.find(column => column.key === 'after').value
      return [
        {
          key: 'locationPlanChange',
          amount: this.monthlyAmountDifference(before.location_base_total, after.location_base_total),
        },
        ...this.monthlyResourceRows.map(row => ({
          key: row.changeKey,
          amount: this.monthlyAmountDifference(before[row.amount], after[row.amount]),
          extra: row.extra,
          beforeIncluded: before[row.included],
          afterIncluded: after[row.included],
          beforeExtra: before[row.extra],
          afterExtra: after[row.extra],
        })),
      ].filter(change => change.amount !== 0)
    },
    monthlyAccountChange() {
      return this.monthlyAmountDifference(
          this.invoicePreview?.monthly_breakdown_before?.total,
          this.invoicePreview?.monthly_breakdown_after?.total
      )
    },
    selectedPlan() {
      return this.plans.find(plan => plan.key === this.selectedPlanKey) || this.plans[0]
    },
    currentPlan() {
      return this.plans.find(plan => plan.key === this.currentPlanKey) || null
    },
    reactivationMode() {
      return this.subscription?.status_key === 'canceled'
    },
    activationMode() {
      return this.reactivationMode || this.isTrial
    },
    cancelPendingMode() {
      return (!this.activationMode && !!this.subscription?.has_pending_plan && this.selectedPlanKey === this.currentPlanKey)
    },
    requiresPlanChangeConfirmation() {
      if (this.trialPlanSelectionOnly) {
        return false
      }

      return ['upgrade', 'downgrade', 'activation'].includes(this.changeDirection)
    },
    planChangeConfirmationLabel() {
      if (this.changeDirection === 'downgrade') {
        return this.$t('billing.planChangeModal.downgradeConfirmation', {
          plan: this.selectedPlanKey,
          date: this.effectiveDateLabel,
        })
      }

      return this.$t(
          this.isTrial
              ? 'billing.planChangeModal.trialActivationConfirmation'
              : this.changeDirection === 'activation'
                  ? 'billing.planChangeModal.activationConfirmation'
                  : 'billing.planChangeModal.upgradeConfirmation'
      )
    },
    trialPlanSelectionOnly() {
      return this.isTrial && !this.trialSelectionCompletesActivation
    },
    shouldLoadInvoicePreview() {
      return ['upgrade', 'downgrade'].includes(this.changeDirection)
          || (
              this.isTrial
              && this.trialSelectionCompletesActivation
          )
    },
    showsInvoicePreview() {
      return this.shouldLoadInvoicePreview && this.changeDirection !== 'downgrade'
    },
    invoicePreviewAmountLabel() {
      if (
          this.isTrial
          && Number(this.invoicePreview?.invoices_count || 0) > 1
      ) {
        return this.$t(
            'billing.planChangeModal.trialInvoicesAmountLabel'
        )
      }

      return this.$t(
          this.isTrial
              ? 'billing.planChangeModal.trialInvoiceAmountLabel'
              : 'billing.planChangeModal.upgradeInvoiceAmountLabel'
      )
    },
    invoicePreviewPeriodLabel() {
      return this.$t(
          this.isTrial
              ? 'billing.planChangeModal.trialChargePeriodLabel'
              : 'billing.planChangeModal.upgradeChargePeriodLabel'
      )
    },
    canContinue() {
      if (!this.selectedPlanKey) {
        return false
      }

      if (this.activationMode) {
        return true
      }

      if (this.selectedPlanKey === this.currentPlanKey) {
        return this.cancelPendingMode
      }

      if (this.subscription?.has_pending_plan && this.selectedPlanKey === this.pendingPlanKey) {
        return false
      }

      if (this.changeDirection === 'downgrade' && !this.subscription?.next_billing_period_start) {
        return false
      }

      return true
    },
    changeDirection() {
      if (this.activationMode) {
        return 'activation'
      }

      if (this.cancelPendingMode) {
        return 'cancel_pending'
      }

      if (!this.currentPlan || !this.selectedPlan) return null

      if (this.selectedPlan.order > this.currentPlan.order) {
        return 'upgrade'
      }

      if (this.selectedPlan.order < this.currentPlan.order) {
        return 'downgrade'
      }

      return 'same'
    },
    selectionHint() {
      if (this.cancelPendingMode) {
        return this.$t('billing.planChangeModal.hints.cancelPending', { plan: this.pendingPlanKey })
      }

      if (this.subscription?.has_pending_plan && this.selectedPlanKey === this.pendingPlanKey) {
        return this.$t('billing.planChangeModal.hints.alreadyScheduled', { plan: this.pendingPlanKey }
        )
      }

      if (this.changeDirection === 'activation') {
        if (this.isTrial) {
          return this.$t(
              this.trialPlanSelectionOnly
                  ? 'billing.planChangeModal.hints.trialSelection'
                  : 'billing.planChangeModal.hints.trialActivation'
          )
        }

        return this.$t('billing.planChangeModal.hints.activation')
      }

      if (this.changeDirection === 'upgrade') {
        return this.$t('billing.planChangeModal.hints.upgrade')
      }

      if (this.changeDirection === 'downgrade') {
        if (!this.subscription?.next_billing_period_start) {
          return this.$t('billing.planChangeModal.hints.downgradeDateMissing')
        }

        return this.$t(
            'billing.planChangeModal.hints.downgrade',
            {date: this.effectiveDateLabel}
        )
      }

      return this.$t('billing.planChangeModal.hints.selectAnotherPlan')
    },
    effectiveDateLabel() {
      if (this.changeDirection === 'activation') {
        if (this.trialPlanSelectionOnly) {
          return this.$t(
              'billing.planChangeModal.trialSelectionEffectiveAfterPayment'
          )
        }

        return this.$t(
            this.isTrial
                ? 'billing.planChangeModal.trialActivationEffectiveImmediately'
                : 'billing.planChangeModal.activationEffectiveImmediately'
        )
      }

      if (this.changeDirection === 'upgrade') {
        return this.$t('billing.planChangeModal.upgradeEffectiveImmediately')
      }

      if (
          this.changeDirection === 'downgrade'
          && this.downgradeEffectiveAt
      ) {
        return this.formatUtcDay(this.downgradeEffectiveAt)
      }

      return ''
    },
    currentPlanValidThroughLabel() {
      if (
          this.changeDirection !== 'downgrade'
          || !this.downgradeEffectiveAt
      ) {
        return ''
      }

      return this.formatUtcDay(
          this.downgradeEffectiveAt - 1
      )
    },
    confirmButtonLabel() {
      if (this.cancelPendingMode) {
        return this.$t('billing.planChangeModal.confirmCancelPendingButton')
      }

      if (this.trialPlanSelectionOnly) {
        return this.$t(
            'billing.planChangeModal.confirmTrialPlanSelectionButton'
        )
      }

      if (this.changeDirection === 'activation') {
        return this.$t(
            this.isTrial
                ? 'billing.planChangeModal.confirmTrialActivationButton'
                : 'billing.planChangeModal.confirmActivationButton'
        )
      }

      return this.changeDirection === 'upgrade'
          ? this.$t('billing.planChangeModal.confirmUpgradeButton')
          : this.$t('billing.planChangeModal.scheduleDowngradeButton')
    },
  },
}
</script>

<style scoped lang="scss">
/* Use one rhythm for both ordinary rows and table cells. */
.plan-change-confirmation {
  --confirmation-row-padding: .75rem;
  --confirmation-border: var(--ws-border, var(--surface-border, #dee2e6));
}
.plan-change-costs { overflow-x: auto; }
.plan-change-costs__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.plan-change-costs__label-column, .plan-change-costs__value-column { width: 50%; }
.plan-change-costs__table--comparison .plan-change-costs__label-column { width: 40%; }
.plan-change-costs__table--comparison .plan-change-costs__value-column { width: 30%; }
.plan-change-costs__table caption {
  text-align: left;
  font-weight: 600;
  padding: .5rem 0;
}
.plan-change-costs__table th, .plan-change-costs__table td {
  padding: var(--confirmation-row-padding) 0;
  border-bottom: 1px solid var(--confirmation-border);
  text-align: right;
  vertical-align: middle;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.plan-change-costs__table th:not(:first-child), .plan-change-costs__table td { padding-left: 1rem; }
.plan-change-costs__table th:first-child { text-align: left; font-weight: 400; }
.plan-change-costs__table tbody th { color: var(--text-color-secondary, #6c757d); }
.plan-change-costs__table .plan-change-costs__total,
.plan-change-costs__table .plan-change-costs__total th { font-weight: 600; }
.plan-change-costs__table--changes .plan-change-costs__label-column { width: 70%; }
.plan-change-costs__table--changes .plan-change-costs__value-column { width: 30%; }
.plan-change-costs__saving { color: var(--green-700, #277442); }
.plan-change-costs__table--details tbody tr:last-child { font-weight: 400; }
.plan-change-costs__discount,
.plan-change-costs__calculation {
  margin-top: .15rem;
  color: var(--text-color-secondary, #6c757d);
  font-size: .85em;
  font-weight: 400;
  line-height: 1.3;
}
.plan-change-costs__discount-price {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: .35rem;
}
.plan-change-cost-details summary {
  cursor: pointer;
  color: var(--primary-color, #00b9ff);
  padding: .35rem 0;
}
.plan-change-cost-details__content { padding-top: .5rem; }
@media (max-width: 600px) {
  .plan-change-costs__table th:not(:first-child), .plan-change-costs__table td { padding-left: .5rem; }
}



//.plan-change-modal__subtitle {
//  margin-bottom: 3px;
//  font-size: 0.9rem;
//  //font-weight: 400;
//}

.plan-change-modal__location {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.35;
}

.plan-change-modal__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.2rem;
  font-size: 0.9rem;
  line-height: 1.35;

  strong {
    color: var(--ws-text, var(--text-color));
  }
}

.plan-change-modal__separator {
  margin: 0 0.1rem;
}

.plan-change-modal__content {
  //padding-top: 0.75rem;
  color: var(--ws-text, var(--text-color));
}

.plan-change-modal__context {
  //display: flex;
  //flex-wrap: wrap;
  //align-items: center;
  //justify-content: space-between;
  gap: 0.5rem 1rem;
  margin-bottom: 1rem;
}

.plan-change-modal__pending {
  margin-top: 0 !important;
  color: var(--orange-700);
}

.plan-change-modal__feature-notice,
.plan-change-confirmation__notice {
  display: flex;
  align-items: center;
  margin-bottom: 1rem !important;
  border: 1px solid var(--ws-border, var(--surface-border));
  border-radius: 6px;
  padding: 0.75rem 0.875rem;
  background: var(--ws-target-row, var(--surface-hover));
  color: var(--ws-text, var(--text-color));
}

.plan-change-confirmation__notice {
  padding: 1rem 1.25rem;
  line-height: 1.55;
  margin-bottom: 1.25rem !important;
}

.plan-change-confirmation__cost-note {
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.plan-change-modal__table-scroll {
  overflow-x: auto;
  border: 1px solid var(--ws-border, var(--surface-border));
  border-radius: 6px;
  background: var(--ws-bg, var(--surface-card));
}

.plan-comparison-table {
  width: 100%;
  min-width: 1080px;
  border-collapse: collapse;
  table-layout: fixed;
}

.plan-comparison-table th,
.plan-comparison-table td {
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid var(--ws-border-soft, var(--surface-border));
  background: var(--ws-bg, var(--surface-card));
  color: var(--ws-text, var(--text-color));
  font-size: 0.9rem;
  text-align: center;
  vertical-align: middle;
}

.plan-comparison-table thead th {
  padding: 0;
  background: var(--ws-header-bg, var(--surface-50));
  cursor: pointer;
}

.plan-comparison-table__feature-column {
  width: 36%;
  text-align: left !important;
}

.plan-comparison-table thead .plan-comparison-table__feature-column {
  padding: 0.75rem;
  color: var(--ws-text-muted, var(--text-color-secondary));
  font-weight: 600;
  cursor: default;
}

.plan-comparison-table__plan-header {
  min-height: 96px;
  padding: 0.65rem 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.plan-comparison-table thead th:not(.plan-column--selected):hover {
  background: var(--ws-side-bg, var(--surface-hover)) !important;
}

.plan-comparison-table__plan-name {
  font-size: 0.9rem;
  font-weight: 700;
}

.plan-comparison-table__price-line {
  margin-top: 0.2rem;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.2rem;
}

.plan-comparison-table__plan-price {
  font-size: 1.25rem;
  font-weight: 700;
}

.plan-comparison-table__plan-period {
  color: var(--ws-text-muted, var(--text-color-secondary));
  font-size: 0.78rem;
}

.plan-comparison-table__plan-action {
  height: 34px;
  margin-top: 0.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.plan-comparison-table__plan-badge {
  margin-top: 0;
  padding: 0.15rem 0.55rem;
  border-radius: 10px;
  background: var(--primary-color);
  color: var(--primary-color-text);
  font-size: 0.72rem;
}

.plan-comparison-table__section-row td {
  padding: 0.6rem 0.75rem;
  border-top: 1px solid var(--ws-border, var(--surface-border));
  border-bottom: 1px solid var(--ws-border, var(--surface-border));
  background: var(--ws-header-bg, var(--surface-50));
  font-weight: 700;
  //text-align: right;
}

.plan-comparison-table__section-row td.plan-comparison-table__section-toggle {
  cursor: pointer;
  color: var(--primary-color);
}

.plan-comparison-table__section-row td.plan-comparison-table__section-toggle i {
  color: var(--primary-color);
  font-size: 0.8rem;
}

.plan-comparison-table__section-row td.plan-comparison-table__section-toggle:hover {
  background: var(--ws-side-bg, var(--surface-hover));
}

.plan-comparison-table__feature-label {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  line-height: 1.35;
}

.plan-comparison-table__info {
  width: 1.25rem;
  height: 1.25rem;
  margin-left: 0.3rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--ws-text-muted, var(--text-color-secondary));
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 1.25rem;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.plan-comparison-table__info .pi {
  display: block;
  font-size: 1rem;
  line-height: 1;
}

.plan-comparison-table__info:hover,
.plan-comparison-table__info:focus-visible {
  color: var(--primary-color);
}

.plan-comparison-table__value--yes {
  color: var(--primary-color);
  font-weight: 700;
}

.plan-comparison-table__value--no {
  color: var(--ws-text-muted, var(--text-color-secondary));
  font-size: 0.72rem;
}

.plan-comparison-table__shared-value {
  text-align: center;
}

.plan-comparison-table__plan-cell {
  cursor: pointer;
}

.plan-column--current {
  background: var(--ws-side-bg, var(--surface-50)) !important;
}

.plan-column--selected {
  background: var(--ws-target-row, var(--surface-hover)) !important;
}

//.plan-comparison-table__feature-row:nth-of-type(even) > td:not(.plan-column--selected) {
//  background: color-mix(
//          in srgb,
//          var(--primary-color) 2.5%,
//          transparent
//  );
//}

.plan-comparison-table__feature-row:hover > td {
  background: color-mix(
          in srgb,
          var(--primary-color) 9%,
          transparent
  ) !important;
}

.plan-comparison-table__feature-row:hover > td.plan-column--selected {
  background: color-mix(
          in srgb,
          var(--primary-color) 17%,
          transparent
  ) !important;
}

.plan-comparison-table__feature-row--required > td {
  background: color-mix(
          in srgb,
          var(--primary-color) 12%,
          var(--ws-bg, var(--surface-card))
  ) !important;
  //border-top: 1px solid var(--primary-color);
  //border-bottom-color: var(--primary-color);
}

.plan-comparison-table__feature-row--required > td:first-child {
  box-shadow: inset 4px 0 0 var(--primary-color);
  color: var(--primary-color);
  font-weight: 600;
}

.plan-comparison-table__feature-row--required > td.plan-column--selected {
  background: color-mix(
          in srgb,
          var(--primary-color) 20%,
          var(--ws-bg, var(--surface-card))
  ) !important;
}

.plan-comparison-table__feature-row--divider > td {
  border-bottom-width: 2px;
  border-bottom-color: var(--ws-border, var(--surface-border));
}

.plan-comparison-table thead .plan-column--selected .plan-comparison-table__plan-header {
  box-shadow: inset 0 3px 0 var(--primary-color);
}

.plan-comparison-table__bottom-actions td {
  padding-top: 0.7rem;
  padding-bottom: 0.7rem;
  border-top: 1px solid var(--ws-border, var(--surface-border));
  border-bottom: 0;
  background: var(--ws-header-bg, var(--surface-50));
  font-weight: 600;
}

.plan-change-modal__notes {
  margin-top: 0.65rem !important;
  color: var(--ws-text-muted, var(--text-color-secondary));
  font-size: 0.8rem;
}

.plan-change-modal__tooltip-content {
  max-width: 420px;
  line-height: 1.45;
  color: var(--text-color);
}

.plan-change-modal__footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.plan-change-modal__footer-summary {
  min-width: 0;
  text-align: left;
}

.plan-change-modal__footer-hint {
  margin-top: 0.2rem;
  color: var(--ws-text-muted, var(--text-color-secondary));
  font-size: 0.82rem;
}

.plan-change-modal__footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  flex-shrink: 0;
}

.plan-change-confirmation {
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  padding: 1rem 0 1.5rem;
}

.plan-change-confirmation__title {
  font-size: 1.2rem;
  font-weight: 600;
}

.plan-change-confirmation__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: var(--confirmation-row-padding) 0;
  border-bottom: 1px solid var(--confirmation-border);
}
.plan-change-confirmation__row > strong {
  text-align: right;
  font-variant-numeric: tabular-nums;
}


.plan-change-confirmation__acceptance {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
